import React, { FC } from "react";
import { FormattedMessage } from "react-intl";
import styled from "@emotion/styled";
import { DefaultTheme } from "common/components/ui";
import { InfoIcon } from "common/components/icons";
import { SHIPMENT_PROBLEMS_LABELS } from "../ShipmentProblemlabels";

const ProblemTable = styled.table<{ theme: DefaultTheme }>`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: ${({ theme }) => theme.spacing.S4};
  background: none;
`;

const TableHeader = styled.th<{ theme: DefaultTheme }>`
  text-align: left;
  padding: ${({ theme }) => `${theme.spacing.S3} 0`};
  color: ${({ theme }) => theme.colors.NEUTRAL[400]};
  font-weight: 600;
  background: none;
  border: none;
  border-bottom: 2px solid ${({ theme }) => theme.colors.NEUTRAL[80]};
  &:first-of-type {
    width: 220px;
    min-width: 160px;
    max-width: 260px;
  }
  &:not(:first-of-type) {
    text-align: right;
  }
`;

const TableHeaderRow = styled.tr<{ theme: DefaultTheme }>`
  border-bottom: 2px solid ${({ theme }) => theme.colors.NEUTRAL[80]};
`;

const TableCell = styled.td<{ theme: DefaultTheme }>`
  padding: ${({ theme }) => `${theme.spacing.S3} 0`};
  vertical-align: middle;
  border: none;
  background: none;
  &:first-of-type {
    text-align: left;
  }
`;

const FirstRowCell = styled(TableCell)<{ theme: DefaultTheme }>`
  border-bottom: 1px solid ${({ theme }) => theme.colors.NEUTRAL[80]};
`;

const DiscrepancyCell = styled(TableCell)`
  text-align: right;
  min-width: 48px;
`;

const FirstRowDiscrepancyCell = styled(DiscrepancyCell)<{ theme: DefaultTheme }>`
  border-bottom: 1px solid ${({ theme }) => theme.colors.NEUTRAL[80]};
`;

const DiscrepancyBadge = styled.span<{ theme: DefaultTheme }>(
  ({ theme }) => `
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.S1};
  background-color: ${theme.colors.RED[100]};
  color: ${theme.colors.RED[500]};
  padding: ${theme.spacing.S1} ${theme.spacing.S2};
  border-radius: ${theme.border.radius.R4};
  font-weight: ${theme.font.weight.BOLD};
  font-size: ${theme.font.size.F3};
`
);

const DiscrepancyInfo = styled(InfoIcon)<{ theme: DefaultTheme }>(
  ({ theme }) => `
  margin-left: ${theme.spacing.S1};
  font-size: ${theme.font.size.F4};
  color: ${theme.colors.RED[500]};
  max-width: 217px;
  max-height: 90px;
`
);

const HeaderWithInfo = styled.div<{ theme: DefaultTheme }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.S1};
  color: ${({ theme }) => theme.colors.NEUTRAL[500]};

  svg {
    color: ${({ theme }) => theme.colors.NEUTRAL[500]};
  }
`;

const ProblemTitle = styled.span<{ theme: DefaultTheme }>(
  ({ theme }) => `
  font-weight: ${theme.font.weight.BOLD};
`
);

const ProblemDescription = styled.div<{ theme: DefaultTheme }>(
  ({ theme }) => `
  color: ${theme.colors.NEUTRAL[400]};
  font-size: ${theme.font.size.F2};
  font-weight: ${theme.font.weight.REGULAR};
`
);

const MessageContainer = styled.div<{ theme: DefaultTheme }>(
  ({ theme }) => `
  max-width: 220px;
  text-align: left;
  font-size: ${theme.font.size.F2};
`
);

interface ShipmentProblemsTableProps {
  palletQuantity: number;
  palletVerifiedQuantity: number;
  checkedOutAt: string;
  isDataLoading?: boolean;
  fullCustodyTimeStamp?: string;
  metrics?: {
    boxSummary?: {
      numBoxesExpected?: number;
      numBoxesReceived?: number;
      numBoxesMissing?: number;
      numBoxesLost?: number;
      numBoxesForwarding?: number;
      numBoxesPendingAtFc?: number;
      numBoxesOther?: number;
    };
  } | null;
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) {
    return "-";
  }
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US");
};

export const ShipmentProblemsTable: FC<ShipmentProblemsTableProps> = ({
  palletQuantity,
  palletVerifiedQuantity,
  checkedOutAt,
  isDataLoading = false,
  fullCustodyTimeStamp,
  metrics,
}) => {
  const palletDiscrepancy =
    palletQuantity != null && palletVerifiedQuantity != null ? palletVerifiedQuantity - palletQuantity : null;

  const boxExpected = metrics?.boxSummary?.numBoxesExpected ?? 0;
  const boxReceived =
    (metrics?.boxSummary?.numBoxesReceived ?? 0) +
    (metrics?.boxSummary?.numBoxesForwarding ?? 0) +
    (metrics?.boxSummary?.numBoxesPendingAtFc ?? 0) +
    (metrics?.boxSummary?.numBoxesOther ?? 0);
  const boxMissing = metrics?.boxSummary?.numBoxesMissing ?? 0;
  const boxLost = metrics?.boxSummary?.numBoxesLost ?? 0;
  const boxDiscrepancy = boxReceived - boxExpected;

  const hasPalletDiscrepancy = typeof palletDiscrepancy === "number" && palletDiscrepancy !== 0;
  const hasBoxDiscrepancy = boxDiscrepancy !== 0 || boxMissing > 0 || boxLost > 0;
  const hasShipmentProblems = hasPalletDiscrepancy || hasBoxDiscrepancy;
  const hasTwoRows = hasPalletDiscrepancy && hasBoxDiscrepancy;

  if (!hasShipmentProblems) {
    return null;
  }

  if (isDataLoading) {
    return null;
  }

  const CellComponent = hasTwoRows ? FirstRowCell : TableCell;
  const DiscrepancyCellComponent = hasTwoRows ? FirstRowDiscrepancyCell : DiscrepancyCell;

  return (
    <ProblemTable>
      <thead>
        <TableHeaderRow>
          <TableHeader>
            <FormattedMessage id="inbounds.shipmentProblem.table.header.problemType" defaultMessage="Problem Type" />
          </TableHeader>
          <TableHeader>
            <FormattedMessage id="inbounds.shipmentProblem.table.header.expected" defaultMessage="Expected" />
          </TableHeader>
          <TableHeader>
            <FormattedMessage id="inbounds.shipmentProblem.table.header.received" defaultMessage="Received" />
          </TableHeader>
          <TableHeader>
            <FormattedMessage id="inbounds.shipmentProblem.table.header.discrepancy" defaultMessage="Discrepancy" />
          </TableHeader>
          <TableHeader>
            <HeaderWithInfo>
              <FormattedMessage id="inbounds.shipmentProblem.table.header.missing" defaultMessage="Missing" />
              <InfoIcon
                placement="top"
                message={
                  <MessageContainer>
                    <FormattedMessage
                      id={SHIPMENT_PROBLEMS_LABELS.missingTooltip.id}
                      defaultMessage={SHIPMENT_PROBLEMS_LABELS.missingTooltip.defaultMessage}
                    />
                  </MessageContainer>
                }
              />
            </HeaderWithInfo>
          </TableHeader>
          <TableHeader>
            <HeaderWithInfo>
              <FormattedMessage id="inbounds.shipmentProblem.table.header.lost" defaultMessage="Lost" />
              <InfoIcon
                placement="top"
                message={
                  <MessageContainer>
                    <FormattedMessage
                      id={SHIPMENT_PROBLEMS_LABELS.lostTooltip.id}
                      defaultMessage={SHIPMENT_PROBLEMS_LABELS.lostTooltip.defaultMessage}
                    />
                  </MessageContainer>
                }
              />
            </HeaderWithInfo>
          </TableHeader>
          <TableHeader>
            <FormattedMessage id="inbounds.shipmentProblem.table.header.updated" defaultMessage="Updated" />
          </TableHeader>
        </TableHeaderRow>
      </thead>
      <tbody>
        {hasPalletDiscrepancy && (
          <tr>
            <CellComponent>
              <ProblemTitle>
                <FormattedMessage
                  id={SHIPMENT_PROBLEMS_LABELS.palletDiscrepancyTitle.id}
                  defaultMessage={SHIPMENT_PROBLEMS_LABELS.palletDiscrepancyTitle.defaultMessage}
                />
              </ProblemTitle>
              <ProblemDescription>
                <FormattedMessage
                  id={SHIPMENT_PROBLEMS_LABELS.palletDiscrepancyDescription.id}
                  defaultMessage={SHIPMENT_PROBLEMS_LABELS.palletDiscrepancyDescription.defaultMessage}
                />
              </ProblemDescription>
            </CellComponent>
            <CellComponent style={{ textAlign: "right" }}>
              <FormattedMessage
                id="inbounds.shipmentProblem.table.palletQuantity"
                defaultMessage="{quantity} pallets"
                values={{ quantity: palletQuantity }}
              />
            </CellComponent>
            <CellComponent style={{ textAlign: "right" }}>
              <FormattedMessage
                id="inbounds.shipmentProblem.table.palletVerifiedQuantity"
                defaultMessage="{quantity} pallets"
                values={{ quantity: palletVerifiedQuantity }}
              />
            </CellComponent>
            <DiscrepancyCellComponent>
              <DiscrepancyBadge>
                {`${palletDiscrepancy}`}
                <DiscrepancyInfo
                  placement="top"
                  message={
                    <MessageContainer>
                      <FormattedMessage
                        id={SHIPMENT_PROBLEMS_LABELS.discrepancyTooltip.id}
                        defaultMessage={SHIPMENT_PROBLEMS_LABELS.discrepancyTooltip.defaultMessage}
                      />
                    </MessageContainer>
                  }
                />
              </DiscrepancyBadge>
            </DiscrepancyCellComponent>
            <CellComponent style={{ textAlign: "right" }}>{Math.abs(palletDiscrepancy)}</CellComponent>
            <CellComponent style={{ textAlign: "right" }}>
              <FormattedMessage id="inbounds.shipmentProblem.table.noDiscrepancy" defaultMessage="-" />
            </CellComponent>
            <CellComponent style={{ textAlign: "right" }}>{formatDate(checkedOutAt)}</CellComponent>
          </tr>
        )}
        {hasBoxDiscrepancy && (
          <tr>
            <TableCell>
              <ProblemTitle>
                <FormattedMessage
                  id="inbounds.shipmentProblem.table.boxDiscrepancy.title"
                  defaultMessage="Box Discrepancy"
                />
              </ProblemTitle>
              <ProblemDescription>
                <FormattedMessage
                  id="inbounds.shipmentProblem.table.boxDiscrepancy.description"
                  defaultMessage="Operations box count doesn't match shipping plan count"
                />
              </ProblemDescription>
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              <FormattedMessage
                id="inbounds.shipmentProblem.table.boxExpected"
                defaultMessage="{quantity} boxes"
                values={{ quantity: boxExpected }}
              />
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              <FormattedMessage
                id="inbounds.shipmentProblem.table.boxReceived"
                defaultMessage="{quantity} boxes"
                values={{ quantity: boxReceived }}
              />
            </TableCell>
            <DiscrepancyCell>
              {boxDiscrepancy !== 0 ? (
                <DiscrepancyBadge>
                  {`${boxDiscrepancy}`}
                  <DiscrepancyInfo
                    placement="top"
                    message={
                      <MessageContainer>
                        <FormattedMessage
                          id={SHIPMENT_PROBLEMS_LABELS.discrepancyTooltip.id}
                          defaultMessage={SHIPMENT_PROBLEMS_LABELS.discrepancyTooltip.defaultMessage}
                        />
                      </MessageContainer>
                    }
                  />
                </DiscrepancyBadge>
              ) : (
                <FormattedMessage id="inbounds.shipmentProblem.table.noDiscrepancy" defaultMessage="-" />
              )}
            </DiscrepancyCell>
            <TableCell style={{ textAlign: "right" }}>
              {boxMissing > 0 ? (
                boxMissing
              ) : (
                <FormattedMessage id="inbounds.shipmentProblem.table.noDiscrepancy" defaultMessage="-" />
              )}
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              {boxLost > 0 ? (
                boxLost
              ) : (
                <FormattedMessage id="inbounds.shipmentProblem.table.noDiscrepancy" defaultMessage="-" />
              )}
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>{formatDate(fullCustodyTimeStamp)}</TableCell>
          </tr>
        )}
      </tbody>
    </ProblemTable>
  );
};
